// /rapportvakt — the landing page.
//
// Order: the promise, what it takes to get one, who is watched, then the thing
// itself. The example is last of the four because it is the payoff, not the
// pitch: a reader who has read the steps and recognised a logo is the one who
// will actually read a delivery.
//
// The product is an email, so the example is an email. What is inside the frame
// is the app's own Delivery component, not a second rendering of the same
// content: if a delivery looks a certain way in the product, this is where a
// reader sees that look for the first time, and the two must not drift.

import React from 'react';
import { Link } from 'react-router-dom';
import RapportvaktShell, { useRvLang } from '../components/rapportvakt/Shell';
import { Delivery, PublisherMark } from '../components/rapportvakt/Delivery';
import {
  RAPPORTVAKT_PROOF_SLUG,
  RAPPORTVAKT_PUBLISHERS,
  RAPPORTVAKT_PUBLISHERS_PATH,
  getDeliveryBySlug,
  rapportvaktPublisherPath,
  rapportvaktSamplePath,
  rapportvaktTabPath,
} from '../config/rapportvakt';
import { getRapportvaktSeo } from '../config/seoRoutes';

/**
 * The example delivery, inside a light mail frame.
 *
 * The frame is the only thing this file draws: a sender row and a subject line,
 * enough for a reader to recognise what they are looking at. Everything under it
 * is <Delivery>, the same component the app renders, so the example cannot say
 * one thing while the product does another.
 */
function EmailCard({ delivery }) {
  const { t } = useRvLang();
  return (
    <div className="rv-mail">
      <div className="rv-mail__bar">
        <span className="rv-mail__from">
          <span className="rv-mail__avatar" aria-hidden="true">
            PL
          </span>
          Punktlista
        </span>
      </div>
      <div className="rv-mail__subject">{t.mailSubject(delivery.title)}</div>

      <div className="rv-mail__body">
        <Delivery delivery={delivery} />
      </div>
    </div>
  );
}

/** One sentence of the headline, with the accented words if they fall here. */
function HeroLine({ text, mark }) {
  const at = mark ? text.indexOf(mark) : -1;
  if (at < 0) return <span className="rv-hero__line">{text}</span>;
  return (
    <span className="rv-hero__line">
      {text.slice(0, at)}
      <span className="rv-hero__accent">{mark}</span>
      {text.slice(at + mark.length)}
    </span>
  );
}

/**
 * The h1: one sentence, with a few words carrying the warm accent.
 *
 * The sentence comes from a single string in strings.js (`heroTitle`), split
 * on sentence boundaries so a copy change that ever adds a second sentence
 * still gets its own line rather than wrapping wherever the column happens to
 * run out. The accented words are a slice of that same string (`heroMark`),
 * so headline and accent cannot drift apart and a translation moves the
 * accent without touching this file. If the slice is not found the headline
 * still renders, in one ink.
 *
 * The accent is the text colour and nothing else. A yellow swipe behind the
 * words was the first thing the eye landed on, and it landed on decoration.
 */
function HeroTitle({ text, mark }) {
  const lines = text.split(/(?<=\.)\s+/).filter(Boolean);
  return (
    <h1>
      {lines.map((line) => (
        <HeroLine key={line} text={line} mark={mark} />
      ))}
    </h1>
  );
}

function LandingBody() {
  const { t } = useRvLang();
  const proof = getDeliveryBySlug(RAPPORTVAKT_PROOF_SLUG);
  const publishers = RAPPORTVAKT_PUBLISHERS;

  return (
    <main>
      {/* The hero sits outside rv__inner so its warm wash runs the full width of
          the window rather than stopping at the 1200px column. */}
      <section className="rv-hero">
        <div className="rv__inner">
          <HeroTitle text={t.heroTitle} mark={t.heroMark} />
          <div className="rv-hero__ctas">
            {/* Straight to the catalogue: the first thing to do is pick. The
                one button on the page a reader meets first, so it carries
                more weight than the standard .rv-btn size. */}
            <Link className="rv-btn rv-btn--primary rv-btn--lg" to={rapportvaktTabPath('bevakningar')}>
              {t.heroCta}
            </Link>
            {/* A plain link, not a second button (Osama's review, 2026-09):
                the page keeps its buttons to the two real actions, this
                is just a way to look before picking. */}
            <Link className="rv-hero__link" to={rapportvaktSamplePath(RAPPORTVAKT_PROOF_SLUG)}>
              {t.seeSample}
            </Link>
          </div>
        </div>
      </section>

      <div className="rv__inner">
        {/* Straight under the promise: what it takes to be sent one of these
            mails. The CTA skips the app's first tab and lands on the catalogue,
            where step 1 actually happens. */}
        {/* No button here (Osama's review, 2026-09): the hero above and the
            close section below already carry the page's two CTAs, so this
            is just the three steps, centred. */}
        <section className="rv-start" aria-labelledby="rv-start-head">
          <h2 className="rv-start__head" id="rv-start-head">
            {t.startHead}
          </h2>
          <ol className="rv-start__steps">
            {t.startSteps.map((step, index) => (
              <li className="rv-start__step" key={step}>
                <span className="rv-start__num" aria-hidden="true">
                  {index + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </section>

        {/* Each mark is a link to that publisher's page: the strip was already
            the one place on the landing page naming all 15, so it is where a
            reader who recognises one of them will click. */}
        <section className="rv-strip" aria-label={t.wallLabel}>
          <div className="rv-strip__logos">
            {publishers.map((publisher) => (
              <Link
                className="rv-strip__logo"
                key={publisher.slug}
                to={rapportvaktPublisherPath(publisher.slug)}
                title={publisher.name}
                aria-label={publisher.name}
              >
                <PublisherMark publisher={publisher.name} />
              </Link>
            ))}
          </div>
          <p className="rv-strip__caption">
            {t.stripCaption}{' '}
            <Link to={RAPPORTVAKT_PUBLISHERS_PATH}>{t.publishersTitle}</Link>
          </p>
        </section>

        {/* The payoff, set apart by a hairline: everything above is the pitch,
            this is the product. */}
        <section className="rv-showcase" aria-labelledby="rv-showcase-head">
          <h2 className="rv-showcase__head" id="rv-showcase-head">
            {t.proofHead}
          </h2>
          <EmailCard delivery={proof} />
        </section>

        <section className="rv-close">
          <h2 className="rv-close__title">{t.closeTitle}</h2>
          <Link className="rv-btn rv-btn--primary" to={rapportvaktTabPath('bevakningar')}>
            {t.heroCta}
          </Link>
        </section>
      </div>
    </main>
  );
}

export default function RapportvaktLandingPage() {
  const seo = getRapportvaktSeo();

  return (
    <RapportvaktShell seo={seo}>
      <LandingBody />
    </RapportvaktShell>
  );
}
